
export default class Auth
{
	static Model;

	static TwoFactorEnabled = false;
	static TwoFactorConfirmed = undefined;
	static NeedsTwoFactorConfirmed = false;
	static TwoFactorInvalid = false;
	static User;

	static RefreshRequest;
	static LoginRequest;
	static LogoutRequest;

	static TalentUpdateRequest;
	static IsLoaded = false;
	static DidExpire = false;
	static Intended;

	static ErrorHandler = null;

	static LoadingPath = '/';
	static LoginPath = '/login';
	static AuthorizePath = '/authorize';

	/**
     * [Note]
     *
     * @returns {String}
     */
	static appropriate()
	{
		if (!this.Intended
			&& !this.loaded())
		{
			this.captureAsIntended();
		}

		if (!this.loaded()) {
			return this.LoadingPath;
		}
		if (this.shouldValidate2FA() && this.TwoFactorConfirmed === false) {
			return this.AuthorizePath;
		}
		if (!this.exists() || this.DidExpire) {
			return this.LoginPath;
		}

		//
	}

	/**
     * [Note]
     *
     * @param {Object} location
     * @returns {Boolean}
     */
	static isAppropriate(location)
	{
		return true;
	}

	/**
     * [Note]
     *
     * @returns {void}
     */
	static captureAsIntended()
	{
		let currentLocation = TB.location
			? TB.location
			: window.location;

		this.Intended = currentLocation;
	}

	/**
     * [Note]
     *
     * @returns {String}
     */
	static intended()
	{
        if (!this.Intended)
        {
        	return {
				pathname: this.appropriate(),
			};
        }

        return {
			...this.Intended
		}
	}

	/**
     * [Note]
     *
     * @returns {Boolean}
     */
	static loaded() {
		return this.IsLoaded;
	}

	/**
	 * [Note]
	 *
	 * @returns {Boolean}
	 */
	static shouldValidate2FA() {
		return laravel?.config?.ENABLE_2FA
	}


	/**
	 * [Note]
	 *
	 * @returns {Boolean}
	 */
	static needsValidation() {
		if (!this.shouldValidate2FA()) {
			return false
		}
		return this.NeedsTwoFactorConfirmed;
	}


	/**
	 * [Note]
	 *
	 * @returns {Boolean}
	 */
	static isValidated() {
		if (!this.shouldValidate2FA()) {
			return true
		}
		return this.TwoFactorConfirmed;
	}

	/**
     * [Note]
     *
     * @returns {Boolean}
     */
	static exists() {
		let user = this.user();

		return TB.isDefined(user);
	}

	/**
     * [Note]
     *
     * @returns new User()
     */
	static user() {
		if (!this.User) {
			return;
		}

        if (!TB.isDefined(this.User?.is_auth)) {
            this.User.is_auth = true;
        }
		if (!(this.User instanceof this.Model)) {
            this.User = this.Model.make({...this.User})
        }

		return this.User;
	}

	/**
     * [Note]
     *
     * @returns {Promise}
     */
	static async onExpired()
	{

	}

	/**
     * [Note]
     *
     * @returns {String}
     */
	static async expire()
	{
		this.DidExpire = true;
		this.User = undefined;
		this.TwoFactorConfirmed = undefined;

		this.captureAsIntended();

		await this.onExpired();

		TB.event('Auth@expired');
	}

	/**
     * [Note]
     *
     * @returns {Promise}
     */
	static async onRefresh()
	{

	}

	/**
     * [Note]
     *
     * @returns {Promise}
     */
	static async refresh()
	{
		if (!this.IsLoaded && laravel.auth) {
			this.User = { ...laravel.auth };

			this.IsLoaded = true;
			TB.event('Auth@load');

			return this.user();
		}

		this.RefreshRequest = TB.request(
			TB.api_endpoint('/api/sessions/current')
		);

		let response = await this.RefreshRequest.get().catch(
			console.warn
		);

		// Handle response error
		if (!response || !TB.has(response, 'success.data.auth')) {

			// broadcast load event
			if (!this.IsLoaded) {
				this.IsLoaded = true;
				TB.event('Auth@load');
			}

			return false;
		}

		// Get user from successful response
		this.User = { ...response.success.data.auth };
		if (this.shouldValidate2FA()) {
			this.TwoFactorConfirmed = true;
		}
		this.DidExpire = false;

		await this.onRefresh();

		// broadcast load event
		if (!this.IsLoaded) {
			this.IsLoaded = true;
			TB.event('Auth@load');
		}

		return this.user();
	}

	/**
     * [Note]
     *
     * @returns {Promise}
     */
	static async onUpdate()
	{

	}

	/**
     * [Note]
     *
     * @param {Object} props
     * @param silent
     * @returns {Promise}
     */
	static async update(props, silent = false)
	{
		this.User = { ...props };

		await this.onUpdate();

		if (!silent) {
            TB.event('Auth@update');
        }

		return this.user();
	}

	/**
     * [Note]
     *
     * @returns {Promise}
     */
	static async onLogin()
	{
        if (TB.isDefined(this.ErrorHandler)){
            // Attach to the client object
            let {id, email, type} = this.User;
            this.ErrorHandler.user = {
                id, email, type
            }
        }
	}

	/**
     * [Note]
     *
     * @param {Object} creds
     * @returns {Promise}
     */
	static async login(creds)
	{
		this.LoginRequest = TB.request(
			TB.api_endpoint('/api/sessions/create'), { ...creds }
		);

		let response = await this.LoginRequest.post().catch(
			console.warn
		);

		let needs2FA = response && this.checkResponseRequires2FA(response)
		if (needs2FA) {
			return;
		}

        console.log('login', response)
		if (!response)
		{
            this.LoginRequest.didHandleError = await this.LoginRequest.handleError();
            if (this.LoginRequest.didHandleError && this.LoginRequest.shouldRerun) {
                return this.login(creds);
            }

			return false;
		}

		this.User = { ...response.success.data.auth };
		this.DidExpire = false;

		await this.onLogin();

		TB.event('Auth@login');

		return this.user();
	}

	/**
     * [Note]
     *
     * @returns {Promise}
     */
	static async onLogout()
	{
        if (TB.isDefined(this.ErrorHandler)){
            this.ErrorHandler.user = {};
			this.TwoFactorConfirmed = undefined;
        }
	}

	/**
     * [Note]
     *
     * @returns {Promise}
     */
	static async logout()
	{
		this.LogoutRequest = TB.request(
			TB.api_endpoint('/api/sessions/destroy')
		);

		let response = await this.LogoutRequest.post().catch(
			console.warn
		);

		// Handle response error
		if (!response && !this.DidExpire) {
            this.LogoutRequest.didHandleError = await this.LogoutRequest.handleError();
            if (this.LogoutRequest.didHandleError
                && this.LogoutRequest.shouldRerun) {
                return this.logout();
            }

			return false;
		}

		await this.onLogout();
        this.User = undefined;
		this.TwoFactorConfirmed = undefined;
        this.DidExpire = false;
        TB.event('Auth@logout');
	}

	/**
     * [Note]
     *
     * @param {Object} response
     */
	static checkResponseRequires2FA(response)
	{
		// console.log("TB @ checkResponseRequires2FA", response);
		if (!this.shouldValidate2FA()) {
			// console.log("TB @ checkResponseRequires2FA -> disabled");
			return false;
		}

		if (response?.success?.message !== '2fa_code_sent'
			&& response?.message !== '2fa_code_sent')  {
			// console.log("TB @ checkResponseRequires2FA -> not in response message", response?.success?.message, response?.message);
			return false;
		}

		this.NeedsTwoFactorConfirmed = true;
		this.TwoFactorConfirmed = false;

		TB.event('Auth@2FAValidate');
		// console.log("TB @ checkResponseRequires2FA -> Auth@2FAValidate");

		return true;
	}

	/**
	 * [Note]
	 *
	 * @returns {Promise}
	 */
	static async validate2FA(props)
	{
		console.log('validate2FA', this.isValidated())
		if (this.isValidated()) {
			return true;
		}
		this.Validate2FARequest = TB.request(
			TB.api_endpoint('/api/sessions/2fa/validate'), { ...props }
		);

		let response = await this.Validate2FARequest.post().catch(
			console.warn
		);

		console.log('validate2FA', this.Validate2FARequest.statusCode())

		if (!response) {
			if (this.Validate2FARequest.statusCode() === 401) {
				this.DidExpire = true;
				this.User = undefined;
				this.TwoFactorConfirmed = undefined;

				await this.onExpired();

				return TB.event('Auth@expired');
			} else if(this.Validate2FARequest.statusCode() === 422) {
				this.TwoFactorConfirmed = false;
				return TB.event('Auth@2FAInvalid');
			}

			this.Validate2FARequest.didHandleError = await this.Validate2FARequest.handleError();

			if (this.Validate2FARequest.didHandleError && this.Validate2FARequest.shouldRerun) {
				return this.validate2FA(props);
			}

			return false;
		}

		this.TwoFactorConfirmed = true;
		this.User = { ...response.success.data.auth };
		this.DidExpire = false;

		await this.onLogin();

		TB.event('Auth@2FAValidate');

		return this.user();
	}

	/**
	 * [Note]
	 *
	 * @returns {Promise}
	 */
	static async refresh2FA()
	{
		this.Refresh2FARequest = TB.request(
			TB.api_endpoint('/api/sessions/2fa/resend')
		);

		let response = await this.Refresh2FARequest.post().catch(
			console.warn
		);

		if (!response) {
			if (this.Refresh2FARequest.statusCode() === 401) {
				this.DidExpire = true;
				this.User = undefined;
				this.TwoFactorConfirmed = undefined;

				await this.onExpired();

				return TB.event('Auth@expired');
			}
			this.Refresh2FARequest.didHandleError = await this.Refresh2FARequest.handleError();
			if (this.Refresh2FARequest.didHandleError && this.Refresh2FARequest.shouldRerun) {
				return this.refresh2FA();
			}
			throw new Error("Something went wrong")
		}

		return response
	}
}